import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { http } from '@/lib/api';
import i18n from '@/i18n';

const AuthContext = createContext(null);

function syncLanguageFromUser(u) {
  const lang = u?.language;
  if (lang && lang !== i18n.language) {
    i18n.changeLanguage(lang);
    try { localStorage.setItem('bp_lang', lang); } catch { /* noop */ }
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // ── Production auth: session lives in httpOnly cookies only. We probe
  // /auth/me on mount; if it 200s, we're authenticated. If it 401s, no user.
  // The axios interceptor performs a silent refresh automatically, so a stale
  // access token gets rotated transparently.
  const refresh = useCallback(async () => {
    try {
      const { data } = await http.get('/auth/me');
      setUser(data);
      syncLanguageFromUser(data);
    } catch {
      setUser(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await http.post('/auth/login', { email, password });
    // 2FA challenge — caller handles the challenge_token flow
    if (data && data.requires_2fa) return data;
    setUser(data.user);
    syncLanguageFromUser(data.user);
    return data.user;
  };

  const verify2FA = async (challenge_token, code, remember_device) => {
    const { data } = await http.post('/auth/2fa/verify', { challenge_token, code, remember_device: !!remember_device });
    setUser(data.user);
    syncLanguageFromUser(data.user);
    return data.user;
  };

  const register = async (email, password, name, opts = {}) => {
    const body = { email, password, name };
    if (opts.role) body.role = opts.role;
    if (opts.invite_token) body.invite_token = opts.invite_token;
    const { data } = await http.post('/auth/register', body);
    setUser(data.user);
    syncLanguageFromUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try { await http.post('/auth/logout'); } catch { /* noop */ }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, login, verify2FA, register, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
